import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { MapPin, Phone, Mail, Clock, Send, ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";
import PageBanner from "../components/PageBanner";
import { contactPageData } from "../data/pageData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", service: "", message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, formData);
      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageBanner
        title="Contact Us"
        subtitle="Get a Free Consultation & Instant Quote"
        image={contactPageData.heroImage}
        breadcrumbs={[{ label: "Contact" }]}
      />

      {/* Contact Info + Form */}
      <section className="inner-section" ref={ref}>
        <div className="inner-container">
          <div className="contact-page-grid">
            <motion.div className="contact-page-info" initial={{ opacity: 0, x: -30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6 }}>
              <h2 className="section-title">Get In Touch</h2>
              <div className="section-line" />
              <p className="contact-page-desc">
                Ready to bring your designs to life? Contact us for a free consultation and quote
                on your 3D printing project. Our team responds within 2 hours during business hours.
              </p>
              <div className="contact-info-cards">
                <div className="cic-item">
                  <div className="cic-icon"><MapPin size={22} /></div>
                  <div>
                    <strong>Visit Our Office</strong>
                    <p>{contactPageData.address}</p>
                  </div>
                </div>
                <div className="cic-item">
                  <div className="cic-icon"><Phone size={22} /></div>
                  <div>
                    <strong>Call Us</strong>
                    <p><a href={`tel:${contactPageData.phone}`}>{contactPageData.phone}</a></p>
                  </div>
                </div>
                <div className="cic-item">
                  <div className="cic-icon"><Mail size={22} /></div>
                  <div>
                    <strong>Email Us</strong>
                    <p><a href={`mailto:${contactPageData.email}`}>{contactPageData.email}</a></p>
                  </div>
                </div>
                <div className="cic-item">
                  <div className="cic-icon"><Clock size={22} /></div>
                  <div>
                    <strong>Working Hours</strong>
                    <p>{contactPageData.workingHours}</p>
                  </div>
                </div>
              </div>

              {/* Embedded Map */}
              <div className="contact-map">
                <iframe
                  title="3D Printzkart Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.4!2d77.52!3d13.03!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae3d0e574c16e9%3A0x1!2sPeenya%20Industrial%20Area%2C%20Bangalore!5e0!3m2!1sen!2sin!4v1"
                  width="100%" height="250" style={{ border: 0, borderRadius: 4 }}
                  allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </motion.div>

            <motion.div className="contact-page-form" initial={{ opacity: 0, x: 30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.15 }}>
              <h3>Request a Free Quote</h3>
              {submitted && <div className="form-success">Thank you! We'll respond within 2 hours during business hours.</div>}
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <input type="text" placeholder="Your Name *" required value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })} />
                  <input type="email" placeholder="Your Email *" required value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })} />
                </div>
                <div className="form-row">
                  <input type="tel" placeholder="Phone Number *" required value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })} />
                  <select value={formData.service} required
                    onChange={e => setFormData({ ...formData, service: e.target.value })}>
                    <option value="">Select Service *</option>
                    <option value="fdm">FDM 3D Printing</option>
                    <option value="sla">SLA 3D Printing</option>
                    <option value="sls">SLS 3D Printing</option>
                    <option value="dmls">DMLS Metal Printing</option>
                    <option value="product">Product Development</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <textarea placeholder="Describe your project requirements..." rows={5}
                  value={formData.message}
                  onChange={e => setFormData({ ...formData, message: e.target.value })} />
                <button type="submit" className="btn-primary" disabled={submitting}
                  style={{ width: '100%', justifyContent: 'center' }}>
                  <Send size={16} /> {submitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="inner-section bg-light">
        <div className="inner-container">
          <span className="section-subtitle">Frequently Asked</span>
          <h2 className="section-title">Common Questions About 3D Printing</h2>
          <div className="section-line" />
          <div className="faq-list">
            {contactPageData.faqItems.map((faq, i) => (
              <div key={i} className={`faq-item ${openFaq === i ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{faq.q}</span>
                  {openFaq === i ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openFaq === i && <div className="faq-answer"><p>{faq.a}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
